/**
 * Utility to flatten deeply nested tables in TipTap/ProseMirror JSON content.
 *
 * Some imported documents (e.g. Word invention disclosure forms) use nested
 * tables to lay out multi-page forms.  TipTap's table extension performs
 * expensive column-width recalculations for every nesting level, and images
 * inside deeply nested cells trigger cascading reflows that can freeze the
 * browser.
 *
 * This utility walks the document tree and extracts any table nested beyond
 * `maxDepth` (default 1) out of its parent cell, placing it as a sibling node
 * after the outermost ancestor table.  The transformation preserves all
 * content — it only changes the nesting structure.
 */

import { JSONContent } from "@tiptap/react";

/** Default maximum allowed table nesting depth (0 = top-level table). */
const DEFAULT_MAX_DEPTH = 1;

/**
 * Flatten nested tables in a TipTap JSONContent document.
 *
 * Tables nested deeper than `maxDepth` are extracted and placed after their
 * ancestor table at the document level.  Returns a new object (the input is
 * not mutated).
 *
 * @param doc       TipTap JSONContent document (type === "doc")
 * @param maxDepth  Maximum allowed nesting depth (default 1, meaning a table
 *                  inside a table is allowed but a table inside a table inside
 *                  a table is not).
 */
export function flattenNestedTables(
  doc: JSONContent,
  maxDepth: number = DEFAULT_MAX_DEPTH
): JSONContent {
  if (!doc || doc.type !== "doc" || !Array.isArray(doc.content)) {
    return doc;
  }

  const newContent: JSONContent[] = [];

  for (const topNode of doc.content) {
    if (topNode.type === "table") {
      // Process this table and collect any extracted nested tables
      const extracted: JSONContent[] = [];
      const processed = processTable(topNode, 0, maxDepth, extracted);
      newContent.push(processed);
      // Append extracted tables after the parent table
      newContent.push(...extracted);
    } else {
      newContent.push(topNode);
    }
  }

  return { ...doc, content: newContent };
}

/**
 * Recursively process a table node.  Any tables found inside cells beyond
 * `maxDepth` are removed from the cell and added to `extracted`.
 */
function processTable(
  table: JSONContent,
  currentDepth: number,
  maxDepth: number,
  extracted: JSONContent[]
): JSONContent {
  if (!table.content) return table;

  const newRows: JSONContent[] = [];

  for (const row of table.content) {
    if (row.type !== "tableRow" || !row.content) {
      newRows.push(row);
      continue;
    }

    const newCells: JSONContent[] = [];

    for (const cell of row.content) {
      if (
        (cell.type !== "tableCell" && cell.type !== "tableHeader") ||
        !cell.content
      ) {
        newCells.push(cell);
        continue;
      }

      const newCellContent: JSONContent[] = [];

      for (const child of cell.content) {
        if (child.type === "table") {
          if (currentDepth + 1 > maxDepth) {
            // This table is too deeply nested — extract it.
            // Recursively process it too (it may contain further nesting).
            const processedChild = processTable(
              child,
              0, // reset depth since it will be a top-level table
              maxDepth,
              extracted
            );
            extracted.push(processedChild);
          } else {
            // Nesting is within limits — keep it but process its children
            const processedChild = processTable(
              child,
              currentDepth + 1,
              maxDepth,
              extracted
            );
            newCellContent.push(processedChild);
          }
        } else {
          newCellContent.push(child);
        }
      }

      // Ensure cell has at least one paragraph (ProseMirror requirement)
      if (newCellContent.length === 0) {
        newCellContent.push({ type: "paragraph" });
      }

      newCells.push({ ...cell, content: newCellContent });
    }

    newRows.push({ ...row, content: newCells });
  }

  return { ...table, content: newRows };
}

/**
 * Check whether a JSONContent document has tables nested deeper than maxDepth.
 * Useful for deciding whether flattening is needed before paying the cost.
 */
export function hasDeeplyNestedTables(
  doc: JSONContent,
  maxDepth: number = DEFAULT_MAX_DEPTH
): boolean {
  if (!doc || !doc.content) return false;

  function checkNode(node: JSONContent, tableDepth: number): boolean {
    if (node.type === "table") {
      if (tableDepth > maxDepth) return true;
      tableDepth++;
    }
    if (node.content) {
      for (const child of node.content) {
        if (checkNode(child, tableDepth)) return true;
      }
    }
    return false;
  }

  return checkNode(doc, 0);
}
